import { Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { FrontendLoggerService } from '../frontend-logger.service';
import { AuthFlowOrchestratorService } from './auth-flow-orchestrator.service';
import {
  AuthFlowStateService,
  PendingInviteContext,
  ResolvedAuthFlow,
} from './auth-flow-state.service';
import { isProviderOAuthCallbackPath } from './provider-oauth-callback.util';

export class AuthPostAuthenticationNavigator {
  private navigationInFlight = false;

  constructor(
    private readonly auth0: Auth0Service,
    private readonly router: Router,
    private readonly flowState: AuthFlowStateService,
    private readonly orchestrator: AuthFlowOrchestratorService,
    private readonly logger: FrontendLoggerService
  ) {}

  isWizardCompleted(): Observable<boolean> {
    const claims$ = (this.auth0 as any).idTokenClaims$ as Observable<any> | undefined;
    return (claims$ || of(null)).pipe(
      map((claims) => {
        const metadata = claims?.['https://www.innovadoc-ia.com/app_metadata'] || null;
        const value = metadata
          ? metadata.WizarCompleted ?? metadata.WizardCompleted
          : null;
        const completed = value === true
          || (typeof value === 'string' && /^\s*(true|1|yes)\s*$/i.test(value));
        this.logger.debug(
          `[AuthService.isWizardCompleted] Strict check. ns present: ${!!metadata}, value: ${JSON.stringify(value)}, result: ${completed}, claims keys: ${JSON.stringify(claims ? Object.keys(claims) : null)}`
        );
        return completed;
      }),
      catchError(() => of(false))
    );
  }

  setPendingInviteContext(token: string, email: string): void {
    this.flowState.setPendingInviteContext(token, email);
  }

  getPendingInviteContext(): PendingInviteContext | null {
    return this.flowState.getPendingInviteContext();
  }

  getPendingInviteTarget(): string | null {
    return this.flowState.getPendingInviteTarget();
  }

  clearPendingInviteContext(): void {
    this.flowState.clearPendingInviteContext();
  }

  setResolvedFlow(flow: ResolvedAuthFlow): void {
    this.flowState.setResolvedFlow(flow);
  }

  getResolvedFlow(): ResolvedAuthFlow | null {
    return this.flowState.getResolvedFlow();
  }

  clearResolvedFlow(): void {
    this.flowState.clearResolvedFlow();
  }

  resolveLegacyTarget(defaultTarget: string): string {
    return this.flowState.getPendingInviteTarget() || defaultTarget;
  }

  resolveTarget(defaultTarget: string): Observable<string> {
    return this.orchestrator.resolvePostAuthTarget(defaultTarget).pipe(
      catchError((error) => {
        this.logger.error(
          '[AuthService] Error resolving post-auth target',
          error?.message || JSON.stringify(error)
        );
        return of(this.resolveLegacyTarget(defaultTarget));
      })
    );
  }

  route(defaultTarget: string, source: string): void {
    if (this.navigationInFlight) {
      this.logger.debug(
        `[AuthService] Post-auth navigation already in flight. Skipping ${source}`
      );
      return;
    }

    this.navigationInFlight = true;
    this.resolveTarget(defaultTarget).pipe(take(1)).subscribe({
      next: (target) => {
        this.logger.info(`[AuthService] Post-auth route resolved from ${source}`, target);
        this.router.navigateByUrl(target)
          .catch((error) => {
            this.logger.error(
              '[AuthService] Error navigating after auth',
              error?.message || JSON.stringify(error)
            );
          })
          .finally(() => { this.navigationInFlight = false; });
      },
      error: (error) => {
        this.navigationInFlight = false;
        this.logger.error(
          '[AuthService] Unexpected error during post-auth navigation',
          error?.message || JSON.stringify(error)
        );
      },
    });
  }

  isAuth0CallbackQuery(query: string): boolean {
    return query.includes('code=')
      && query.includes('state=')
      && !this.isProviderOAuthCallbackRoute();
  }

  isProviderOAuthCallbackRoute(): boolean {
    return isProviderOAuthCallbackPath();
  }
}
